// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerToolBarActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/* eslint-disable no-unused-vars */

import { compose, withHandlers, withProps } from 'recompose';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import { get, curry } from 'lodash/fp';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';

/**
 * Check permission of save button.
 * @param {Object} props
 * @returns {boolean} Indicate to Save permission
 */
const checkPermissionOfSaveButton = (props: any): boolean => {
  const {
    store: { model },
    authorization,
    authorizationConstants: { PERMISSION_KEY: permissionKeys, PERMISSION_OPERATOR: permissionOperator }
  } = props;
  const accessPriority = model.accessPriority || ACCESS_RIGHT.FullAccess;
  const rightAccessPriority = accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs;

  if (authorization.checkPrivileges([permissionKeys.FullReportAndDashboardAccess]) && rightAccessPriority) return true;

  if (model.version) {
    return (
      authorization.checkPrivileges(
        [permissionKeys.ReportsCanCreateNewReportValue, permissionKeys.ReportsActionsOverwriteExistingReport],
        permissionOperator.Or
      ) && rightAccessPriority
    );
  }

  return authorization.checkPrivileges([permissionKeys.ReportsCanCreateNewReportValue]) && rightAccessPriority;
};

/**
 * Check permission of save as button.
 * @param {Object} props
 * @returns {boolean} Indicate to Save As permission
 */
const checkPermissionOfSaveAsButton = ({
  authorization,
  authorizationConstants: { PERMISSION_KEY: permissionKeys }
}): boolean => authorization.checkPrivileges([permissionKeys.ReportsCanCreateNewReportValue]);

/**
 * Check to display the Show/Hide Header Footer button
 * @param {string} pageName
 * @param {Object} store
 * @returns {boolean} Indicate to display button
 */
const checkDisplayShowHideHeaderFooter = (pageName: string, store: any): boolean =>
  pageName === constants.PAGE_NAME.FIELDS && get('model.reportFormats.headerFooter.visible', store);

/**
 * Get label of Show/Hide Header Footer button
 * @param {Object} localization
 * @param {Object} store
 * @returns {string} The label of button
 */
const getShowHideHeaderFooterLabel = (localization: any, { isShowingHeaderFooter }): string => {
  const key = isShowingHeaderFooter
    ? 'COMMON_LABEL_HIDE_REPORT_HEADER_FOOTER'
    : 'COMMON_LABEL_SHOW_REPORT_HEADER_FOOTER';
  return localization.getText(key);
};

/**
 * On display Show/Hide Header Footer button handler
 * @param {Object} props
 * @param {Object} e
 */
const onDisplayShowHideHeaderFooter = ({ store }, e: any): void => {
  store.isShowingHeaderFooter = !store.isShowingHeaderFooter;
};

/**
 * Reset alert detail.
 * @param {Object} store
 */
const resetAlertDetails = ({ alertDetails }): void => {
  alertDetails.reset();
};

export default compose(
  withHandlers({
    onDisplayShowHideHeaderFooter: curry(onDisplayShowHideHeaderFooter)
  }),
  withProps({
    checkPermissionOfSaveButton,
    checkPermissionOfSaveAsButton,
    checkDisplayShowHideHeaderFooter,
    getShowHideHeaderFooterLabel,
    resetAlertDetails
  })
);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Izenda.ReportDesignerToolBarActions.js